// @flow

// Builds a standalone HTML document (string) for the Rate Card PDF report.
// Deliberately plain — no React/MobX/Highcharts. The report is printed from
// an isolated `window.open()` popup rather than the live app page, because
// printing a full production SPA (with its own MobX/Relay reactions and
// ResizeObservers) alongside a report can hang Chromium's print pipeline;
// an isolated static document has nothing else running that print can choke on.

import logoUrl from "../../../assets/img/magnit-global-logo.png";
import fontUrl from "../../../assets/fonts/Montserrat-Latin.woff2";
import {
  getExperienceBands,
  hasThreeExperienceLevels,
} from "../../../utils/experienceLevels";

const COLORS = {
  navy: "#36459b",
  payHeader: "#4656a5",
  charcoal: "#2b2b38",
  coral: "#f16752",
  lightBlue: "#9ac3e4",
};

const LEVEL_ORDER = (left, right) =>
  (left.level?.legacyId || 0) - (right.level?.legacyId || 0);

function escapeHtml(value: mixed): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function createRateFormatter(node) {
  const currency = node.currency || {};
  const iso = String(currency.iso || "")
    .trim()
    .toUpperCase();
  let symbol = String(currency.symbol || "").trim();
  if (/^[A-Z]{3}$/.test(iso)) {
    symbol = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: iso,
      currencyDisplay: "symbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency").value;
  }
  const fractionDigits = node.rateType === 1 ? 2 : 0;
  const numberFormat = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: true,
  });
  return (value: ?number, allowWrap: boolean = true) => {
    if (value === null || value === undefined) return "—";
    return `${escapeHtml(symbol || iso || "$")} ${
      allowWrap ? "<wbr>" : ""
    }${numberFormat.format(Number(value))}`;
  };
}

function formatPct(value: ?number) {
  if (value === null || value === undefined) return "—";
  return `${Number(value).toFixed(2)}%`;
}

function levelLabel(rate) {
  return escapeHtml(rate.level?.romanNumeral || "—");
}

function levelSubLabel(rate, experienceBands) {
  return escapeHtml(
    experienceBands[rate.level?.romanNumeral] || rate.level?.literal || ""
  );
}

function locationFor(node) {
  return escapeHtml(
    node.locationLabel ||
      [node.city, node.state, node.countryLabel || node.country]
        .filter(Boolean)
        .join(", ")
  );
}

function effectiveDateFor(node) {
  const latest = (node.marketrates || [])
    .map((rate) => rate.modified)
    .filter(Boolean)
    .sort()
    .reverse()[0];
  if (!latest) return "—";
  const date = new Date(latest);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function scale(value: ?number, factor: ?number) {
  return value != null && factor != null ? value * factor : null;
}

function computeBillRates(
  marketrates,
  payrollMarkupPct: ?number,
  staffingMarkupPct: ?number
) {
  const payrollFactor = payrollMarkupPct != null ? 1 + payrollMarkupPct / 100 : null;
  const staffingFactor = staffingMarkupPct != null ? 1 + staffingMarkupPct / 100 : null;
  return marketrates.map((rate) => ({
    ...rate,
    payrollBillRateMin: scale(rate.payRateMin, payrollFactor),
    payrollBillRateAvg: scale(rate.payRateAvg, payrollFactor),
    payrollBillRateMax: scale(rate.payRateMax, payrollFactor),
    staffingBillRateMin: scale(rate.payRateMin, staffingFactor),
    staffingBillRateAvg: scale(rate.payRateAvg, staffingFactor),
    staffingBillRateMax: scale(rate.payRateMax, staffingFactor),
  }));
}

function curvePath(points) {
  return points
    .map((point, index) => {
      if (index === 0) return `M ${point.positionX} ${point.positionY}`;
      const previous = points[index - 1];
      const midpoint = (previous.positionX + point.positionX) / 2;
      return `C ${midpoint} ${previous.positionY} ${midpoint} ${point.positionY} ${point.positionX} ${point.positionY}`;
    })
    .join(" ");
}

function pointRuns(points) {
  const runs = [];
  let current = [];
  points.forEach((point) => {
    if (point) current.push(point);
    else if (current.length) {
      runs.push(current);
      current = [];
    }
  });
  if (current.length) runs.push(current);
  return runs;
}

function buildLineChartSvg(marketrates, series, formatRate, experienceBands, pageIndex) {
  const width = 720;
  const height = 258;
  const padding = { top: 24, right: 38, bottom: 72, left: 38 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const baseline = padding.top + plotHeight;
  const values = series.flatMap((item) => item.values).filter(Number.isFinite);
  const minimum = Math.min(0, ...values);
  const maximum = Math.max(...values, 1);
  const range = Math.max(maximum - minimum, 1);
  const yFor = (value) => baseline - ((value - minimum) / (range * 1.12)) * plotHeight;
  const xFor = (index) =>
    padding.left +
    (marketrates.length === 1
      ? plotWidth / 2
      : (plotWidth * index) / Math.max(marketrates.length - 1, 1));
  const pointsBySeries = series.map((item) =>
    item.values.map((value, index) =>
      Number.isFinite(value)
        ? { positionX: xFor(index), positionY: yFor(value), value }
        : null
    )
  );
  const gradientId = `rc-pdf-area-${pageIndex}`;
  const grid = Array.from({ length: 4 }, (_, index) => {
    const positionY = padding.top + (plotHeight * index) / 3;
    return `<line x1="${padding.left}" x2="${
      width - padding.right
    }" y1="${positionY}" y2="${positionY}" stroke="#ececf0" />`;
  }).join("");
  const area =
    pointsBySeries.length > 1
      ? pointRuns(
          pointsBySeries[1].map((point, index) =>
            point && pointsBySeries[0][index]
              ? { ...point, lower: pointsBySeries[0][index] }
              : null
          )
        )
          .filter((run) => run.length > 1)
          .map((run) => {
            const lower = run.map((point) => point.lower).reverse();
            return `<path d="${curvePath(run)} L ${lower[0].positionX} ${
              lower[0].positionY
            } ${curvePath(lower)
              .split(" C ")
              .slice(1)
              .map((segment) => `C ${segment}`)
              .join(" ")} Z" fill="url(#${gradientId})" />`;
          })
          .join("")
      : "";
  const lines = series
    .map((item, seriesIndex) => {
      const points = pointsBySeries[seriesIndex];
      const paths = pointRuns(points)
        .map(
          (run) =>
            `<path class="rc-pdf-series-line" d="${curvePath(run)}" fill="none" stroke="${
              item.color
            }" stroke-width="3" />`
        )
        .join("");
      const labels = points
        .map((point, index) => {
          if (!point) return "";
          const anchor =
            index === 0 ? "start" : index === marketrates.length - 1 ? "end" : "middle";
          const label = formatRate(point.value, false);
          const labelWidth = Math.min(
            label.length * 7,
            plotWidth / Math.max(marketrates.length, 1) - 8
          );
          return `<circle cx="${point.positionX}" cy="${point.positionY}" r="4.5" fill="${
            item.color
          }" />
        <text class="rc-pdf-point-value" x="${point.positionX}" y="${
            point.positionY + (seriesIndex === 0 ? 19 : -12)
          }" fill="${
            item.color
          }" text-anchor="${anchor}" font-size="11" font-weight="800" textLength="${labelWidth}" lengthAdjust="spacingAndGlyphs">${label}</text>`;
        })
        .join("");
      return paths + labels;
    })
    .join("");
  const labels = marketrates
    .map((rate, index) => {
      const anchor =
        index === 0 ? "start" : index === marketrates.length - 1 ? "end" : "middle";
      return `<text class="rc-pdf-axis-label" x="${xFor(index)}" y="${
        height - 25
      }" text-anchor="${anchor}" font-size="11" font-weight="700" fill="#95959f">Level ${levelLabel(
        rate
      )}<tspan x="${xFor(index)}" dy="16" fill="${COLORS.navy}">${levelSubLabel(
        rate,
        experienceBands
      ).replace("years", "Yrs")}</tspan></text>`;
    })
    .join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="Average rate by experience level">
    <defs><linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${
    series[series.length - 1].color
  }" stop-opacity="0.17"/><stop offset="100%" stop-color="${
    series[series.length - 1].color
  }" stop-opacity="0.01"/></linearGradient></defs>
    ${grid}${area}${lines}${labels}
  </svg>`;
}

function buildPage(
  rateCard,
  node,
  payrollMarkupPct: ?number,
  staffingMarkupPct: ?number,
  pageIndex
) {
  const formatRate = createRateFormatter(node);
  const isHourly = node.rateType === 1;
  const hasPayroll = isHourly && payrollMarkupPct != null;
  const hasStaffAug = isHourly && staffingMarkupPct != null;
  const isSuggested = isHourly && !hasPayroll && !hasStaffAug;
  const marketrates = computeBillRates(
    (node.marketrates || []).slice().sort(LEVEL_ORDER),
    hasPayroll ? payrollMarkupPct : null,
    hasStaffAug ? staffingMarkupPct : null
  );
  const experienceBands = getExperienceBands(
    hasThreeExperienceLevels(marketrates.map((rate) => rate.level?.romanNumeral))
  );
  const jobTitle = escapeHtml(node.job?.jobLabel || node.job?.jobTitle || "");
  const location = locationFor(node);
  const cityAndState = escapeHtml([node.city, node.state].filter(Boolean).join(", "));
  const country = escapeHtml(node.countryLabel || node.country || "");
  const workerType = hasStaffAug
    ? "Staff Aug"
    : hasPayroll
    ? "Payroll"
    : isSuggested
    ? "Suggested"
    : "FTE";
  const mode = hasStaffAug
    ? "staffaug"
    : hasPayroll
    ? "payroll"
    : isSuggested
    ? "suggested"
    : "fte";
  const metadata = [
    ["Worker Type", workerType],
    ["Rate Type", isHourly ? "Hourly · Contract" : "Annual · FTE"],
  ];
  if (hasPayroll && hasStaffAug)
    metadata.push([
      "Payroll / Staff Aug MU",
      `${Number(payrollMarkupPct)}% / ${Number(staffingMarkupPct)}%`,
    ]);
  else if (hasPayroll) metadata.push(["Payroll MU", `${Number(payrollMarkupPct)}%`]);
  else if (hasStaffAug) metadata.push(["Staff Aug MU", `${Number(staffingMarkupPct)}%`]);
  else if (isSuggested) metadata.push(["Markup", "Suggested"]);
  metadata.push(["Effective", effectiveDateFor(node)]);

  const series = [
    {
      label: "Pay Rate",
      color: COLORS.navy,
      field: "payRateAvg",
      values: marketrates.map((rate) => rate.payRateAvg),
    },
  ];
  if (hasStaffAug)
    series.push({
      label: "Staff Aug Bill Rate",
      color: COLORS.coral,
      field: "staffingBillRateAvg",
      values: marketrates.map((rate) => rate.staffingBillRateAvg),
    });
  else if (hasPayroll)
    series.push({
      label: "Payroll Bill Rate",
      color: COLORS.charcoal,
      field: "payrollBillRateAvg",
      values: marketrates.map((rate) => rate.payrollBillRateAvg),
    });
  else if (isSuggested)
    series.push({
      label: "Bill Rate",
      color: COLORS.coral,
      field: "billRateAvg",
      values: marketrates.map((rate) => rate.billRateAvg),
    });

  const tableGroups = [{ key: "pay", title: "Pay Rate", field: "payRate" }];
  if (isSuggested)
    tableGroups.push(
      {
        key: "suggested",
        title: "Suggested Markup %",
        field: "markupPct",
        percentage: true,
      },
      { key: "bill", title: "Bill Rate", field: "billRate" }
    );
  if (hasPayroll)
    tableGroups.push({
      key: "payroll",
      title: "Payroll Bill Rate",
      field: "payrollBillRate",
      markup: payrollMarkupPct,
    });
  if (hasStaffAug)
    tableGroups.push({
      key: "staffaug",
      title: "Staff Aug Bill Rate",
      field: "staffingBillRate",
      markup: staffingMarkupPct,
    });
  const groupHeaderCells = tableGroups
    .map(
      (group) =>
        `<th class="rc-pdf-group-${group.key}" colspan="2">${group.title}${
          group.markup != null
            ? `<span class="rc-pdf-group-subtitle"> (${Number(group.markup)}% MU)</span>`
            : ""
        }</th>`
    )
    .join("");
  const subHeaderCells = tableGroups
    .flatMap((group) =>
      ["Low", "High"].map(
        (label) => `<th class="rc-pdf-group-${group.key}">${label}</th>`
      )
    )
    .join("");
  const rows = marketrates
    .map((rate) => {
      const cells = tableGroups.flatMap((group) =>
        ["Min", "Max"].map((suffix) => {
          const value = rate[`${group.field}${suffix}`];
          return `<td class="rc-pdf-value-${group.key}">${
            group.percentage ? formatPct(value) : formatRate(value)
          }</td>`;
        })
      );
      return `<tr><td class="rc-pdf-level-cell"><div class="rc-pdf-level-name">Level ${levelLabel(
        rate
      )}</div><div class="rc-pdf-level-sub">${levelSubLabel(
        rate,
        experienceBands
      )}</div></td>${cells.join("")}</tr>`;
    })
    .join("");
  const cards = marketrates
    .map(
      (rate, index) => `<div class="rc-pdf-summary-card">
      <div class="rc-pdf-summary-title">Level ${levelLabel(rate)}</div>
      <div class="rc-pdf-summary-years">${levelSubLabel(rate, experienceBands).replace(
        "years",
        "yrs"
      )}</div>
      <dl>${series
        .map(
          (item, seriesIndex) =>
            `<div><dt>${
              seriesIndex === 0 ? "Pay" : "Bill"
            }</dt><dd class="rc-pdf-summary-value" style="color:${
              item.color
            }">${formatRate(item.values[index])}</dd></div>`
        )
        .join("")}</dl>
    </div>`
    )
    .join("");
  const legend = series
    .map((item) => `<span><i style="background:${item.color}"></i>${item.label}</span>`)
    .join("");
  const levelGuide = marketrates
    .map(
      (rate) =>
        `<li><span>${levelLabel(rate)}</span>${levelSubLabel(rate, experienceBands)}</li>`
    )
    .join("");

  return `<section class="rc-pdf-page rc-pdf-mode-${mode}">
    <aside class="rc-pdf-sidebar">
      <img class="rc-pdf-logo" src="${logoUrl}" alt="Magnit Global" />
      <div class="rc-pdf-brandline">Pay Intel &copy; Rate Analysis</div>
      <div class="rc-pdf-kicker">Client Market Rate Analysis</div>
      <h1><span class="rc-pdf-title-text">${jobTitle}</span></h1>
      <div class="rc-pdf-location">${cityAndState || location}${
    cityAndState && country ? `<strong>${country}</strong>` : ""
  }</div>
      <dl class="rc-pdf-meta-row">${metadata
        .map(
          ([label, value]) =>
            `<div class="rc-pdf-meta-col"><dt class="rc-pdf-meta-label">${label}</dt><dd class="rc-pdf-meta-value">${value}</dd></div>`
        )
        .join("")}</dl>
      <div class="rc-pdf-experience"><h2>Experience Levels</h2><ol>${levelGuide}</ol></div>
    </aside>
    <main class="rc-pdf-body">
      <header class="rc-pdf-main-header"><h2>Wages by Worker Level <span>— ${
        cityAndState || location
      }</span></h2><span class="rc-pdf-badge">${workerType} · ${
    isHourly ? "Hourly" : "Annual"
  }</span></header>
      <div class="rc-pdf-summary" style="grid-template-columns:repeat(${Math.max(
        marketrates.length,
        1
      )}, minmax(0, 1fr))">${cards}</div>
      <section class="rc-pdf-chart">
        <div class="rc-pdf-chart-header"><h2 class="rc-pdf-chart-title">Average Rate by Experience Level</h2><div class="rc-pdf-chart-legend">${legend}</div></div>
        ${buildLineChartSvg(marketrates, series, formatRate, experienceBands, pageIndex)}
      </section>
      <h2 class="rc-pdf-section-title">${location} - Rate Range</h2>
      <table class="rc-pdf-rates">
        <colgroup><col class="rc-pdf-level-column" /><col span="${
          tableGroups.length * 2
        }" /></colgroup>
        <thead><tr><th rowspan="2" class="rc-pdf-level-header"></th>${groupHeaderCells}</tr><tr>${subHeaderCells}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <footer class="rc-pdf-footer"><p>*** All Magnit recommended rates are guides for what Magnit data suggests a role can be filled at. Rates may vary by candidate skill and market demand.</p><p class="rc-pdf-confidential">&copy; ${new Date().getFullYear()} Magnit · Proprietary &amp; Confidential</p></footer>
    </main>
  </section>`;
}

const STYLES = `
  @font-face { font-family: ReportMontserrat; src: url("${fontUrl}") format("woff2"); font-style: normal; font-weight: 100 900; font-display: swap; }
  * { box-sizing: border-box; letter-spacing: 0; }
  body { margin: 0; font-family: ReportMontserrat, sans-serif; color: ${COLORS.charcoal}; }
  .rc-pdf-page, .rc-pdf-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .rc-pdf-page { display: grid; grid-template-columns: 27.6% minmax(0, 1fr); width: 1056px; min-height: 1366px; margin: 0 auto; background: #fff; break-after: page; }
  .rc-pdf-page:last-child { break-after: auto; }
  .rc-pdf-sidebar { padding: 44px 33px; color: #fff; background: ${COLORS.charcoal}; min-width: 0; }
  .rc-pdf-logo { width: 100%; height: auto; display: block; filter: grayscale(1) invert(1) brightness(3); mix-blend-mode: screen; }
  .rc-pdf-brandline { margin: 34px 0 42px; color: ${COLORS.lightBlue}; font-size: 11px; text-transform: uppercase; font-weight: 700; }
  .rc-pdf-kicker { color: #9c9cab; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 12px; }
  .rc-pdf-sidebar h1 { font-size: 24px; line-height: 1.3; font-weight: 800; margin: 0 0 16px; }
  .rc-pdf-title-text { white-space: normal; overflow-wrap: normal; word-break: normal; hyphens: none; }
  .rc-pdf-location { color: #c4c4ce; font-size: 15px; line-height: 1.6; overflow-wrap: anywhere; }
  .rc-pdf-location strong { display: block; color: ${COLORS.lightBlue}; font-weight: 700; }
  .rc-pdf-meta-row { margin: 32px 0; padding: 30px 0; border-top: 1px solid #4d4d5b; border-bottom: 1px solid #4d4d5b; }
  .rc-pdf-meta-col + .rc-pdf-meta-col { margin-top: 24px; }
  .rc-pdf-meta-label, .rc-pdf-experience h2 { color: #9c9cab; text-transform: uppercase; font-size: 10px; line-height: 1.5; font-weight: 700; margin: 0 0 4px; }
  .rc-pdf-meta-value { margin: 0; font-size: 15px; font-weight: 700; line-height: 1.45; overflow-wrap: anywhere; }
  .rc-pdf-mode-staffaug .rc-pdf-meta-col:first-child dd { color: ${COLORS.coral}; }
  .rc-pdf-mode-payroll .rc-pdf-meta-col:first-child dd { color: ${COLORS.lightBlue}; }
  .rc-pdf-experience ol { margin: 12px 0 0; padding: 0; list-style: none; }
  .rc-pdf-experience li { display: flex; align-items: center; gap: 10px; margin: 10px 0; font-size: 11px; font-weight: 700; }
  .rc-pdf-experience li span { display: inline-flex; justify-content: center; align-items: center; flex-shrink: 0; width: 25px; height: 25px; border-radius: 50%; background: ${COLORS.navy}; }
  .rc-pdf-body { min-width: 0; padding: 44px 46px 48px; }
  .rc-pdf-main-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 28px; }
  .rc-pdf-main-header h2 { font-size: 18px; font-weight: 800; margin: 0; line-height: 1.35; min-width: 0; overflow-wrap: anywhere; }
  .rc-pdf-main-header h2 span { color: ${COLORS.navy}; }
  .rc-pdf-badge { flex-shrink: 0; font-size: 11px; line-height: 1.4; font-weight: 800; padding: 7px 12px; border-radius: 24px; background: #f9f4e7; color: ${COLORS.navy}; }
  .rc-pdf-mode-payroll .rc-pdf-badge { background: #e3eef5; }
  .rc-pdf-mode-staffaug .rc-pdf-badge { background: #fbe0db; color: #d75543; }
  .rc-pdf-summary { display: grid; gap: 14px; margin-bottom: 34px; }
  .rc-pdf-summary-card { padding: 18px 14px; border-top: 4px solid ${COLORS.navy}; border-radius: 8px; background: #f1f1f1; min-width: 0; }
  .rc-pdf-summary-title { color: ${COLORS.navy}; font-size: 12px; font-weight: 800; white-space: nowrap; }
  .rc-pdf-summary-years { color: #888892; font-size: 9px; margin: 3px 0 12px; white-space: nowrap; }
  .rc-pdf-summary-card dl { margin: 0; font-size: 10px; }
  .rc-pdf-summary-card dl > div { display: flex; align-items: baseline; justify-content: space-between; gap: 4px; margin-top: 4px; }
  .rc-pdf-summary-card dt { color: #72727f; font-weight: 700; }
  .rc-pdf-summary-card dd { margin: 0; text-align: right; font-weight: 800; min-width: 0; overflow-wrap: anywhere; }
  .rc-pdf-chart { background: #fff; border: 1px solid #ececf0; border-radius: 8px; box-shadow: 0 10px 32px #2b2b3810; padding: 24px 0 14px; break-inside: avoid; }
  .rc-pdf-chart-header { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 12px; padding: 0 24px 16px; }
  .rc-pdf-chart-title { font-size: 15px; font-weight: 700; margin: 0; }
  .rc-pdf-chart-legend { display: flex; flex-wrap: wrap; gap: 14px; color: #72727f; font-size: 10px; font-weight: 700; }
  .rc-pdf-chart-legend span { display: inline-flex; align-items: center; gap: 4px; }
  .rc-pdf-chart-legend i { display: inline-block; width: 9px; height: 9px; border-radius: 50%; }
  .rc-pdf-chart svg { display: block; width: 100%; height: auto; overflow: visible; }
  .rc-pdf-section-title { position: relative; padding-left: 14px; font-size: 15px; font-weight: 800; line-height: 1.5; margin: 32px 0 32px; overflow-wrap: anywhere; }
  .rc-pdf-section-title:before { content: ""; position: absolute; top: 3px; left: 0; width: 4px; height: 16px; border-radius: 2px; background: ${COLORS.coral}; }
  .rc-pdf-rates { width: 100%; table-layout: fixed; border-collapse: collapse; }
  .rc-pdf-level-column { width: 18%; }
  .rc-pdf-rates th { padding: 10px 5px; font-size: 11px; text-transform: uppercase; color: #fff; }
  .rc-pdf-rates thead tr:first-child th { padding: 14px 6px 5px; }
  .rc-pdf-rates .rc-pdf-level-header { background: #fff; }
  .rc-pdf-group-pay { background: ${COLORS.payHeader}; }
  .rc-pdf-group-payroll { background: ${COLORS.navy}; }
  .rc-pdf-group-staffaug, .rc-pdf-group-bill { background: ${COLORS.coral}; }
  .rc-pdf-rates .rc-pdf-group-suggested { background: #f1e8d2; color: ${COLORS.charcoal}; }
  .rc-pdf-group-subtitle { font-weight: 400; }
  .rc-pdf-rates td { border-bottom: 1px solid #ececf0; padding: 16px 6px; height: 65px; text-align: center; font-size: 14px; font-weight: 800; overflow-wrap: anywhere; }
  .rc-pdf-rates tbody tr:nth-child(even) { background: #f9f9fb; }
  .rc-pdf-rates .rc-pdf-level-cell { text-align: left; padding-left: 16px; }
  .rc-pdf-level-name { color: ${COLORS.navy}; font-size: 15px; font-weight: 800; white-space: nowrap; }
  .rc-pdf-level-sub { color: #8c8c97; font-size: 10px; font-weight: 400; margin-top: 3px; white-space: nowrap; }
  .rc-pdf-value-pay { color: ${COLORS.navy}; }
  .rc-pdf-value-staffaug, .rc-pdf-value-bill { color: ${COLORS.coral}; }
  .rc-pdf-footer { margin-top: 18px; padding-top: 10px; border-top: 1px solid #ececf0; color: #95959f; font-size: 9px; line-height: 1.6; }
  .rc-pdf-footer p { margin: 0 0 4px; }
  .rc-pdf-confidential { text-align: right; }
  @page { margin: 0; }
  @media print {
    .rc-pdf-page { width: 100%; min-height: 100vh; margin: 0; }
    .rc-pdf-sidebar { padding: 32px 24px; }
    .rc-pdf-brandline { font-size: 8px; margin: 24px 0 32px; }
    .rc-pdf-kicker { font-size: 9px; margin-bottom: 10px; }
    .rc-pdf-sidebar h1 { font-size: 18px; }
    .rc-pdf-location { font-size: 12px; }
    .rc-pdf-meta-row { margin: 24px 0; padding: 22px 0; }
    .rc-pdf-meta-col + .rc-pdf-meta-col { margin-top: 18px; }
    .rc-pdf-meta-label, .rc-pdf-experience h2 { font-size: 8px; }
    .rc-pdf-meta-value { font-size: 12px; }
    .rc-pdf-experience li { font-size: 9px; gap: 8px; margin: 8px 0; }
    .rc-pdf-experience li span { width: 20px; height: 20px; }
    .rc-pdf-body { padding: 32px 30px; }
    .rc-pdf-main-header { margin-bottom: 22px; gap: 10px; }
    .rc-pdf-main-header h2 { font-size: 14px; }
    .rc-pdf-badge { font-size: 9px; padding: 6px 10px; }
    .rc-pdf-summary { gap: 10px; margin-bottom: 26px; }
    .rc-pdf-summary-card { padding: 14px 10px; border-top-width: 3px; }
    .rc-pdf-summary-title { font-size: 10px; }
    .rc-pdf-summary-years { font-size: 7px; margin-bottom: 10px; }
    .rc-pdf-summary-card dl { font-size: 8px; }
    .rc-pdf-chart { padding-top: 20px; padding-bottom: 12px; }
    .rc-pdf-chart-header { padding: 0 18px 12px; gap: 8px; }
    .rc-pdf-chart-title { font-size: 12px; }
    .rc-pdf-chart-legend { font-size: 8px; gap: 12px; }
    .rc-pdf-chart-legend i { width: 8px; height: 8px; }
    .rc-pdf-section-title { font-size: 12px; margin: 24px 0; }
    .rc-pdf-rates th { font-size: 9px; padding: 8px 4px; }
    .rc-pdf-rates thead tr:first-child th { padding: 12px 4px 4px; }
    .rc-pdf-rates td { font-size: 11px; padding: 12px 4px; height: 50px; }
    .rc-pdf-rates .rc-pdf-level-cell { padding-left: 12px; }
    .rc-pdf-level-name { font-size: 12px; }
    .rc-pdf-level-sub { font-size: 8px; }
    .rc-pdf-footer { font-size: 7px; margin-top: 14px; padding-top: 8px; }
  }
`;

export default function buildRateCardPdfHtml(
  rateCard: ?Object,
  searches: Array<Object>,
  payrollMarkupPct: ?number,
  staffingMarkupPct: ?number
): string {
  const pages = searches
    .map((node, index) =>
      buildPage(rateCard, node, payrollMarkupPct, staffingMarkupPct, index)
    )
    .join("");
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=1056" />
    <title>&nbsp;</title>
    <style>${STYLES}</style>
  </head>
  <body>
    ${pages}
  </body>
</html>`;
}
